// components/rows/FormRows.jsx
import React from "react";
import { useTranslation } from "react-i18next";
import InputField from "../components/InputField";
import SelectDropdown from "../components/SelectDropdown";
import Switch from "../components/Switch";
import CreatableSelect from "react-select/creatable";
import Tooltip from "../components/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { brand, tailwindColors } from "../theme/gitoColors";


// ------------------------------------------------------------
// Base Row (responsive + better alignment + flexible widths)
// ------------------------------------------------------------
const RowLabel = ({ label, tooltip }) => (
    <span className="inline-flex items-center gap-1.5 text-sm text-mute-700 font-normal">
        <span>{label}</span>
        {tooltip && (
            <Tooltip content={tooltip} position="right" tooltipClassName="max-w-lg px-2 py-1">
                <span
                    className="inline-flex shrink-0 cursor-help text-mute-400 hover:text-mute-600"
                    aria-label={tooltip}
                >
                    <FontAwesomeIcon icon={faInfoCircle} className="h-3.5 w-3.5" />
                </span>
            </Tooltip>
        )}
    </span>
);

const Row = ({ label, tooltip, children }) => (
    <div
        className="
      py-4 border-b border-mute-200
      grid grid-cols-1
      sm:grid-cols-[140px_auto]
      md:grid-cols-[160px_auto]
      lg:grid-cols-[180px_auto]
      gap-x-6 gap-y-2
      items-center
    "
    >
        <RowLabel label={label} tooltip={tooltip} />
        <div className="w-full">{children}</div>
    </div>
);

// ------------------------------------------------------------
// Input Row
// ------------------------------------------------------------
export const InputRow = ({ label, tooltip, value, onChange, invalid, isNumber, maxLength, disabled, action }) => (
    <Row label={label} tooltip={tooltip}>
        <div className={action ? "flex w-full flex-col gap-2 sm:flex-row sm:items-center" : "w-full"}>
            <div className={action ? "min-w-0 w-full sm:flex-1" : "w-full"}>
                <InputField
                    value={value}
                    label={label}
                    hideLabel
                    required
                    isInvalid={invalid}
                    isNumber={isNumber}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                    maxLength={maxLength}
                />
            </div>
            {action ? (
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
                    {action}
                </div>
            ) : null}
        </div>
    </Row>
);

// ------------------------------------------------------------
// Select Row
// ------------------------------------------------------------
export const SelectRow = ({
    label,
    tooltip,
    value,
    options,
    required,
    disabled,
    onChange,
    invalid,
    invalidMessage,
    isMulti = false,
    isClearable = false
}) => {
    const selected = isMulti
        ? options.filter((o) => Array.isArray(value) && value.includes(o.value))
        : options.find((o) => o.value === value);

    return (
        <Row label={label} tooltip={tooltip}>
            <SelectDropdown
                options={options}
                selectedValues={selected}
                required={required}
                disabled={disabled}
                isInvalid={invalid}
                invalidMessage={invalidMessage}
                isMulti={isMulti}
                isClearable={isClearable}
                onChange={(sel) => {
                    if (isMulti) {
                        onChange(Array.isArray(sel) ? sel.map((x) => x.value) : []);
                        return;
                    }

                    onChange(sel?.value ?? null);
                }}
            />
        </Row>
    );
};

export const CreatableSelectRow = ({
    label,
    tooltip,
    value,
    options,
    disabled,
    onChange,
    invalid,
    isMulti = false,
    placeholder,
}) => {
    const { t } = useTranslation();
    const customStyles = {
        control: (styles, state) => ({
            ...styles,
            borderColor: invalid ? tailwindColors.error[600] : styles.borderColor,
            boxShadow: invalid
                ? `0 0 0 1px ${tailwindColors.error[600]}`
                : state.isFocused
                    ? styles.boxShadow
                    : "none",
            "&:hover": {
                borderColor: invalid
                    ? tailwindColors.error[600]
                    : styles["&:hover"]?.borderColor,
            },
        }),
        multiValue: (styles) => ({
            ...styles,
            backgroundColor: tailwindColors.primary[300],
            color: brand.darkBlue,
            borderRadius: "12px",
            padding: "2px 8px",
            fontWeight: "600",
        }),
        multiValueLabel: (styles) => ({
            ...styles,
            color: brand.darkBlue,
            padding: 0,
        }),
        multiValueRemove: (styles) => ({
            ...styles,
            color: brand.darkBlue,
            cursor: "pointer",
            ":hover": {
                backgroundColor: tailwindColors.volt[800],
                color: brand.white,
            },
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 99999,
        }),
    };

    return (
        <Row label={label} tooltip={tooltip}>
            <CreatableSelect
                isMulti={isMulti}
                options={options}
                value={
                    isMulti
                        ? (Array.isArray(value) ? value.map(v => ({ label: v, value: v })) : [])
                        : value
                            ? { label: value, value }
                            : null
                }
                onChange={(sel) => {
                    if (isMulti) {
                        onChange(sel ? sel.map(x => x.value) : []);
                    } else {
                        onChange(sel ? sel.value : null);
                    }
                }}
                isClearable
                isDisabled={disabled}
                styles={customStyles}
                className={invalid ? "input-invalid" : ""}
                placeholder={placeholder ?? t("dropdown.select")}
                menuPortalTarget={document.body}
                menuPosition="fixed"
            />
        </Row>


    );
};

// ------------------------------------------------------------
// Radio Row (Yes/No, etc.)
// ------------------------------------------------------------
export const RadioRow = ({ label, tooltip, value, options, required, invalid, onChange }) => (
    <Row label={label} tooltip={tooltip}>
        <div className="flex flex-wrap gap-3">
            {options.map((opt) => (
                <label
                    key={String(opt.value)}
                    className={[
                        "inline-flex items-center gap-2 text-sm select-none cursor-pointer",
                        "rounded-md border px-3 py-2",
                        value === opt.value ? "border-mute-900 bg-mute-50" : "border-mute-300 bg-white hover:bg-mute-50",
                        invalid ? "border-red-500" : "",
                    ].join(" ")}
                >
                    <input
                        type="radio"
                        name={label}
                        value={String(opt.value)}
                        checked={value === opt.value}
                        onChange={() => onChange(opt.value)}
                        className={[
                            "h-4 w-4",
                            invalid ? "accent-red-600" : "accent-mute-900",
                        ].join(" ")}
                        required={required}
                    />
                    <span className={invalid ? "text-red-600" : "text-mute-900"}>{opt.label}</span>
                </label>
            ))}
        </div>
        {invalid && (
            <div className="mt-2 text-sm text-red-600">
                This field is required.
            </div>
        )}
    </Row>
);

// ------------------------------------------------------------
// Switch Row
// ------------------------------------------------------------
export const SwitchRow = ({ label, tooltip, checked, onChange, disabled, invalid, invalidMessage }) => (
    <Row label={label} tooltip={tooltip}>
        <div className="flex flex-col gap-1">
            <Switch checked={checked} onChange={onChange} disabled={disabled} />
            {invalid && invalidMessage && (
                <span className="text-xs text-error-400">{invalidMessage}</span>
            )}
        </div>
    </Row>
);

// ------------------------------------------------------------
// Detail Row (read-only)
// ------------------------------------------------------------
export const DetailRow = ({ label, tooltip, value }) => (
    <div
        className="
      py-4 border-b border-mute-200
      grid grid-cols-1
      sm:grid-cols-[140px_auto]
      md:grid-cols-[160px_auto]
      lg:grid-cols-[180px_auto]
      gap-x-6 gap-y-2
      items-center
    "
    >
        <RowLabel label={label} tooltip={tooltip} />
        <span className="text-sm font-semibold  break-all">
            {value || "-"}
        </span>
    </div>
);

// ------------------------------------------------------------
// Date Row
// ------------------------------------------------------------
export const DateRow = ({ label, tooltip, value, onChange, invalid, invalidMessage }) => (
    <Row label={label} tooltip={tooltip}>
        <div className="flex flex-col gap-1">
            <input
                type="datetime-local"
                className={`input-default w-full ${invalid ? "input-invalid" : ""}`}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
            />
            {invalid && invalidMessage && (
                <span className="text-xs text-error-400">{invalidMessage}</span>
            )}
        </div>
    </Row>
);


// ------------------------------------------------------------
// TextArea Row
// ------------------------------------------------------------
export const TextAreaRow = ({
    label,
    tooltip,
    value,
    onChange,
    invalid,
    rows = 4,
    maxLength,
    placeholder,
    disabled,
}) => (
    <Row label={label} tooltip={tooltip}>
        <textarea
            className={`
                input-default w-full resize-y
                ${invalid ? "border-red-500" : ""}
            `}
            value={value || ""}
            rows={rows}
            maxLength={maxLength}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
        />
    </Row>
);

// ------------------------------------------------------------
// Date-only Row (YYYY-MM-DD)
// ------------------------------------------------------------
export const DateOnlyRow = ({ label, tooltip, value, onChange, invalid, disabled }) => (
    <Row label={label} tooltip={tooltip}>
        <input
            type="date"
            className={`
        input-default w-full
        ${invalid ? "border-red-500" : ""}
      `}
            value={value || ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
        />
    </Row>
);

// ------------------------------------------------------------
// Month Row (YYYY-MM)
// ------------------------------------------------------------
export const MonthRow = ({ label, tooltip, value, onChange, invalid, disabled, errorMessage, min, max }) => (
    <Row label={label} tooltip={tooltip}>
        <div className="flex flex-col gap-1 max-w-md w-full">
            <input
                type="month"
                className={`
        border border-mute-300 rounded px-2 py-1 text-sm w-full
        ${invalid ? "border-red-500" : ""}
      `}
                value={value || ""}
                disabled={disabled}
                min={min}
                max={max}
                onChange={(e) => onChange(e.target.value)}
            />
            {errorMessage ? (
                <span className="text-xs text-red-600">{errorMessage}</span>
            ) : null}
        </div>
    </Row>
);

// ------------------------------------------------------------
// Time Row (HH:mm)
// ------------------------------------------------------------
export const TimeRow = ({ label, tooltip, value, onChange, invalid, disabled, step = 60 }) => (
    <Row label={label} tooltip={tooltip}>
        <input
            type="time"
            className={`
        input-default w-full
        ${invalid ? "border-red-500" : ""}
      `}
            value={value || ""}
            disabled={disabled}
            step={step} // 60 = 1 min. Sätt 1 om du vill kunna sekunder.
            onChange={(e) => onChange(e.target.value)}
        />
    </Row>
);

